import { Box, Stack, Typography } from '@mui/material';
import React from 'react';
import { environment } from '../../../environments/environment';
import {
  getStoreStatus,
  getTodaysClosingTime,
  ensureAmPmFormat,
} from '../../Utils/storeTimingUtils';
import logo from '../../../assets/fattyBattyLogo.png';
import truckImg from '../../../assets/Coffee Truck.png';
import StorefrontIcon from '@mui/icons-material/Storefront';
const LocationCard = ({
  store,
  number,
  onClick,
  isSelected = false,
  userLocation = null,
}) => {
  // Early return if no store data
  if (!store) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', color: '#666' }}>
        <Typography>Store data not available</Typography>
      </Box>
    );
  }

  // Use shared timing utilities for consistent behaviour with BranchItem
  const storeStatus = getStoreStatus(store);
  const closingTime = getTodaysClosingTime(store);

  // Calculate distance with better error handling
  const getDistance = () => {
    if (!userLocation?.lat || !userLocation?.lng) {
      return 'Distance unknown';
    }

    const storeLat = store?._geoloc?.lat || store?.address?.lat;
    const storeLng = store?._geoloc?.lng || store?.address?.lng;

    if (!storeLat || !storeLng) {
      return 'Distance unknown';
    }

    try {
      // Haversine formula for distance calculation (output in miles)
      const R = 3958.8; // Earth's radius in miles
      const dLat = ((storeLat - userLocation.lat) * Math.PI) / 180;
      const dLng = ((storeLng - userLocation.lng) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((userLocation.lat * Math.PI) / 180) *
          Math.cos((storeLat * Math.PI) / 180) *
          Math.sin(dLng / 2) *
          Math.sin(dLng / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      const distanceMiles = R * c;

      // Format distance in miles, using feet for very short distances
      if (distanceMiles < 0.1) {
        // show in feet for values under 0.1 mile
        const feet = Math.round(distanceMiles * 5280);
        return `${feet}ft`;
      } else if (distanceMiles < 1) {
        return `${distanceMiles.toFixed(2)}mi`;
      } else if (distanceMiles < 10) {
        return `${distanceMiles.toFixed(1)}mi`;
      } else {
        return `${Math.round(distanceMiles)}mi`;
      }
    } catch (error) {
      console.error('Distance calculation error:', error);
      return 'Distance unknown';
    }
  };

  const distance = getDistance();
  const isDistanceApproximate =
    userLocation?.method && userLocation.method !== 'GPS';

  const formatUSAddress = (address) => {
    if (!address) return 'Address not available';

    const line1 = address.addressLine1 || address.route || '';
    const city = address.locality || address.city || '';
    const state = address.administrativeDistrictLevel1 || address.state || '';
    const zip = address.postalCode || address.zipCode || '';

    const line2Parts = [];
    if (city) line2Parts.push(city);
    if (state) line2Parts.push(state);

    let line2 = line2Parts.join(', ');
    if (zip) {
      line2 = line2 ? `${line2} ${zip}` : zip;
    }

    const full = [line1, line2].filter(Boolean).join(', ');
    return full || 'Address not available';
  };

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'flex',
        borderRadius: '15px',
        background: '#fff',
        gap: 2,
        border: isSelected
          ? `2px solid ${environment.mainColor}`
          : '1px solid transparent',
        cursor: 'pointer',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        '&:hover': {
          border: `1px solid ${environment.mainColor}`,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      }}
    >
      <Box
        sx={{
          width: '116px',
          height: '114px',
          borderTopLeftRadius: '15px',
          borderBottomLeftRadius: '15px',
          overflow: 'hidden',
        }}
      >
        <img
          src={store?.image || logo}
          alt={store?.businessName || 'Store'}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-around',
          py: 1,
        }}
      >
        <Stack direction="column">
          <Stack
            direction="row"
            alignItems="center"
            justifyContent={'space-between'}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: 16,
                color: '#1D1D21',
                fontFamily: 'poppins, sans-serif',
                lineHeight: '120%',
              }}
            >
              {store?.businessName || 'Store Name'}
            </Typography>
            <Stack direction="row" gap={1} px={2}>
              {(() => {
                const specialDeliveryIds = new Set([
                  'L2AFG5YSPK15Q',
                  'L46X7J42FCWX2',
                ]);
                const isSpecial = specialDeliveryIds.has(store?.id);

                return isSpecial ? (
                  <StorefrontIcon
                    sx={{ color: environment.mainColor, fontSize: 24 }}
                  />
                ) : (
                  <img
                    src={truckImg}
                    alt="Coffee Truck"
                    style={{
                      width: '24px',
                      height: '24px',
                      objectFit: 'cover',
                    }}
                  />
                );
              })()}
            </Stack>
          </Stack>

          <Stack direction="row" gap={1}>
            <Typography sx={{ color: '#878787', fontSize: '12px' }}>
              {distance}
              {isDistanceApproximate && (
                <span style={{ fontSize: '10px', opacity: 0.7 }}> ~</span>
              )}
            </Typography>
            {/* <Typography sx={{ color: '#878787', fontSize: '12px' }}>
              {store?.rating?.aggregate_rating
                ? `${store.rating.aggregate_rating} (${
                    store.rating.reviews || 0
                  } reviews)`
                : 'No reviews'}
            </Typography> */}
          </Stack>
        </Stack>

        <Typography variant="body2" sx={{ color: '#262626', fontSize: '13px' }}>
          {formatUSAddress(store?.address)}
        </Typography>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent={'space-between'}
          gap={1.5}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              flex: 1,
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                backgroundColor: storeStatus.isOpen ? '#E8F5E9' : '#FFEBEE',
                paddingX: 1.5,
                paddingY: 0.5,
                borderRadius: '8px',
                width: 'fit-content',
              }}
            >
              <Box
                sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: storeStatus.isOpen ? '#4CAF50' : '#B91C1C',
                  boxShadow: storeStatus.isOpen
                    ? '0 0 4px rgba(76, 175, 80, 0.5)'
                    : '0 0 4px rgba(185, 28, 28, 0.5)',
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  fontSize: '12px',
                  color: storeStatus.isOpen ? '#2E7D32' : '#B91C1C',
                  fontFamily: 'Jost, sans-serif',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {storeStatus.isOpen ? 'Open Now' : 'Closed'}
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: '11px',
                color: '#666',
                fontFamily: 'Jost, sans-serif',
                fontStyle: 'italic',
                paddingLeft: 0.5,
              }}
            >
              {storeStatus.isOpen ? (
                `Closes at ${ensureAmPmFormat(closingTime)}`
              ) : storeStatus.nextOpenTime ? (
                <>
                  Opens{' '}
                  {storeStatus.nextOpenDay === 'today'
                    ? 'today'
                    : storeStatus.nextOpenDay === 'tomorrow'
                    ? 'tomorrow'
                    : storeStatus.nextOpenDay}{' '}
                  at {storeStatus.nextOpenTime}
                </>
              ) : (
                <span style={{ color: '#B91C1C', fontWeight: 600 }}>
                  Closed indefinitely
                </span>
              )}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '32px',
              height: '32px',
              borderRadius: '8px',
              backgroundColor: '#F5F5F5',
              border: '1px solid #E0E0E0',
              marginRight: 1,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 900,
                fontSize: '14px',
                color: '#172B4D',
                fontFamily: 'Jost, sans-serif',
              }}
            >
              {number}
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default LocationCard;
